import React, { useState, useRef, KeyboardEvent } from 'react'
import { Box, Chip, Stack, Typography, alpha } from '@mui/material'
import { X } from 'lucide-react'

/** The verdict when no caller-supplied `validate` has an opinion. */
const DEFAULT_INVALID_MESSAGE = 'Enter a valid email address.'

export interface EmailChipInputProps {
  value?: string[]
  onChange?: (emails: string[]) => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  /** Optional field label rendered above the box with the standard 8px gap. */
  label?: string
  /**
   * Why this address may not be added, or `null` if it may.
   *
   * ⚠️ Called at the moment of COMMITTING (Enter / comma / space / blur / paste),
   * never on a keystroke. The same reasoning `TransferCertificateModal`'s
   * `listFooter` records at length: a partial address is not a wrong address, so
   * judging one mid-word tells a user typing `spencer@vera.inc` that `.i` is not a
   * real ending. The trigger has to be the user's action.
   *
   * Duplicate detection belongs here when the caller knows about a list this
   * component cannot see (an existing allowlist, a selection made elsewhere). The
   * within-the-field duplicate is checked below regardless, so no caller has to
   * re-implement it.
   */
  validate?: (email: string) => string | null
  /**
   * The uncommitted text, reported on every keystroke.
   *
   * Exists so a submit can be gated on "nothing is still sitting in the box".
   * Without it, an address the field REFUSED to chip is invisible to the parent,
   * and pressing submit drops it silently — which is the one outcome a rejection
   * message does not fix on its own.
   */
  onPendingChange?: (pending: string) => void
}

export const EmailChipInput = ({
  value = [],
  onChange,
  // An EXAMPLE, not instructions. The separator rules belong in the helper text
  // below the field (and every current call site already states them there), so
  // repeating them here said the same thing twice and left the field showing no
  // example of what a valid entry looks like.
  placeholder = 'name@company.com',
  disabled = false,
  error = false,
  label,
  validate,
  onPendingChange,
}: EmailChipInputProps) => {
  const [inputValue, setInputValue] = useState('')
  /**
   * Why the last commit attempt was refused, or null.
   *
   * Self-clearing by construction: any keystroke wipes it, so there is no stale
   * verdict to reset and no timer. Same shape as `questionedInvite` in
   * `TransferCertificateModal`, and for the same reason.
   */
  const [rejection, setRejection] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateEmail = (email: string): boolean => {
    return /\S+@\S+\.\S+/.test(email)
  }

  /** Every write to the uncommitted text goes through here, so the parent cannot miss one. */
  const setPending = (text: string) => {
    setInputValue(text)
    onPendingChange?.(text)
  }

  /** The caller's verdict if it has one, otherwise the syntax check this field has always run. */
  const rejectionFor = (email: string): string | null => {
    if (validate) return validate(email)
    return validateEmail(email) ? null : DEFAULT_INVALID_MESSAGE
  }

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setPending('')
      setRejection(null)
      return
    }

    // Rejected input: KEEP it in the field so a partial email is never silently
    // wiped (e.g. on blur or a mid-typing re-render), and SAY WHY.
    //
    // ⚠️ The message is the part that changed. This used to return silently, so a
    // refused address just sat there looking accepted and was dropped at submit —
    // indistinguishable, from the operator's side, from having been added.
    const reason = rejectionFor(trimmedEmail)
    if (reason) {
      setRejection(reason)
      return
    }

    // A duplicate of something already chipped is refused the same way rather than
    // absorbed. It used to be a silent no-op that cleared the field, which reads as
    // "added" — the same failure one line up.
    if (value.includes(trimmedEmail)) {
      setRejection(`${trimmedEmail} is already in this list.`)
      return
    }

    onChange?.([...value, trimmedEmail])
    setPending('')
    setRejection(null)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget.value

    // Add email on Enter, comma, or space
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault()
      if (input.trim()) {
        addEmail(input)
      }
    }

    // Remove last email on Backspace if input is empty
    if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange?.(value.slice(0, -1))
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')

    // Split by comma, space, or newline
    const candidates = pastedText
      .split(/[\s,\n]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0)

    const accepted: string[] = []
    let refused = 0
    for (const candidate of candidates) {
      if (rejectionFor(candidate) || value.includes(candidate) || accepted.includes(candidate)) {
        refused++
        continue
      }
      accepted.push(candidate)
    }

    if (accepted.length > 0) {
      onChange?.([...value, ...accepted])
    }

    // A paste is the one place a count beats a sentence: twenty addresses can be
    // refused for several reasons at once, and naming one of them would describe
    // the others wrongly. Silence here was the original behaviour and is the worst
    // option — a paste of 20 that adds 12 looks identical to one that adds 20.
    setRejection(
      refused > 0
        ? `${refused} pasted address${refused > 1 ? 'es were' : ' was'} not added. They are invalid or already listed.`
        : null
    )
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }

  const removeEmail = (emailToRemove: string) => {
    onChange?.(value.filter(email => email !== emailToRemove))
    // Removing the thing a rejection was about makes the rejection wrong.
    setRejection(null)
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    /*
      §31 — A FIELD IS ONE ELEMENT, NEVER A FRAGMENT. This returned
      `<>label + box</>`, so inside the `<Stack spacing={n}>` every form here is
      built from, the label and the control were separate children and the Stack's
      gap replaced the 8px this label's own `mb: 2` asks for. The rejection line
      below would have been a third such child. One container, and the parent's
      spacing now separates FIELDS.
    */
    <Box>
    {label && (
      <Typography variant="body3" sx={{ mb: 2, display: 'block' }}>
        {label}
      </Typography>
    )}
    <Box
      onClick={handleContainerClick}
      sx={(theme) => {
        const bg = theme.palette.primary?.[50] || '#f6f6f6'
        return {
          minHeight: '47px',
          backgroundColor: bg,
          borderRadius: '8px',
          padding: '8px 12px',
          display: 'flex',
          // §74 — the chip row WRAPS, and `gap` (not a Stack's `spacing`) is what
          // makes that safe: it sets row-gap and column-gap together, so wrapped
          // rows cannot sit flush. A fixed-width container holding a list that
          // grows is the exact shape that rule exists for, and a modal is where it
          // has nowhere to overflow to.
          flexWrap: 'wrap',
          gap: 1,
          rowGap: 1,
          alignItems: 'center',
          // The box must never be what widens the dialog. `minWidth: 0` lets it
          // shrink inside a flex/grid parent instead of being sized by its content.
          minWidth: 0,
          maxWidth: '100%',
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
          // Keep a 1px border at all times so focus/hover only change its COLOR,
          // never the box size — otherwise the +1px on focus shifts the card.
          // At rest the border matches the background, so it's invisible.
          border: `1px solid ${error ? '#d32f2f' : bg}`,
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            borderColor:
              !disabled && !error
                ? theme.palette.brand?.[600] || '#7F48DF'
                : undefined,
          },
          // Soft brand focus ring (matches @repo/ui Input/TextField): brand
          // border + low-opacity glow, no width change.
          '&:focus-within': {
            borderColor:
              !disabled && !error
                ? theme.palette.brand?.[600] || '#7F48DF'
                : undefined,
            boxShadow:
              !disabled && !error
                ? `0 0 0 3px ${theme.palette.brand?.['600-6%'] || alpha(theme.palette.brand?.[600] || '#7F48DF', 0.16)}`
                : undefined,
          },
        }
      }}
    >
      {value.map((email, index) => (
        <Chip
          key={`${email}-${index}`}
          title={email}
          label={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontSize: '14px' }}>
                {email}
              </Typography>
            </Stack>
          }
          onDelete={disabled ? undefined : () => removeEmail(email)}
          deleteIcon={
            <X
              size={16}
              style={{
                cursor: 'pointer',
                color: '#666',
              }}
            />
          }
          sx={(theme) => ({
            backgroundColor: theme.palette.background?.paper || '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            height: '28px',
            // §74 — a single long address must not push the dialog wide. A chip is
            // one unbreakable token, so wrapping it is not available; capping it at
            // the row and ellipsing is. The full address stays readable in the
            // native title below.
            maxWidth: '100%',
            '& .MuiChip-label': {
              padding: '0 8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .MuiChip-deleteIcon': {
              margin: '0 4px 0 -4px',
              '&:hover': {
                color: '#333',
              },
            },
          })}
        />
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => {
          setPending(e.target.value)
          // A keystroke retracts the last verdict. The address it was about no
          // longer exists, so keeping the message would accuse the new one.
          setRejection(null)
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={value.length === 0 ? placeholder : ''}
        disabled={disabled}
        style={{
          flex: 1,
          minWidth: '120px',
          border: 'none',
          outline: 'none',
          backgroundColor: 'transparent',
          fontSize: '16px',
          padding: '4px',
          fontFamily: 'inherit',
        }}
      />
    </Box>
    {/*
      The verdict, at the point of the control it is about. `role="alert"` because
      it appears in response to an action the user just took, not on load.

      §74 — `overflowWrap: 'anywhere'` because the message QUOTES the address, and
      a 60-character address in a fixed-width dialog is precisely the long
      single-line value that rule names.
    */}
    {rejection && (
      <Typography
        role="alert"
        variant="caption"
        color="error"
        sx={{ display: 'block', mt: 2, lineHeight: 1.5, overflowWrap: 'anywhere' }}
      >
        {rejection}
      </Typography>
    )}
    </Box>
  )
}
